import React, { useState, useEffect, useCallback } from "react";
import useStore from "store";

import { Link } from "react-router-dom";
import styled from 'styled-components';
import axios from 'axios';

const ContainerStyled = styled.div`
	.section-card {
		background: white;
		border-radius: 10px;
		padding: 15px;
		margin-bottom: 15px;
		box-shadow: 0 1px 3px rgba(0,0,0,0.1);

		.section-title {
			font-size: 13px;
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 1px;
			color: #6c757d;
			margin-bottom: 12px;
			padding-bottom: 8px;
			border-bottom: 2px solid #eee;
		}

		.menu-btn {
			display: flex;
			align-items: center;
			justify-content: space-between;
			font-weight: 600;
			font-size: 15px;
			padding: 12px 16px;
			border-radius: 8px;
			margin-bottom: 8px;

			&:last-child {
				margin-bottom: 0;
			}

			.count-badge {
				background: rgba(255,255,255,0.3);
				padding: 2px 10px;
				border-radius: 20px;
				font-size: 14px;
				font-weight: 700;
				min-width: 36px;
				text-align: center;
			}
		}
	}

	.logout-btn {
		display: block;
		width: 100%;
		padding: 12px 16px;
		border-radius: 8px;
		font-weight: 600;
		font-size: 15px;
	}
`;

let cancelTokenSource = null;

export default function Dashboard() {
	const dispatch = useStore(state => state.dispatch);
	const user = useStore(state => state.user);
	const [lastTimeControl, setLastTimeControl] = useState(null);
	const [workorderCounts, setWorkorderCounts] = useState(null);
	const [logisticsCounts, setLogisticsCounts] = useState(null);
	const [logisticsNonSupplyCounts, setLogisticsNonSupplyCounts] = useState(null);

	const loadDashboard = useCallback(() => {
		// Time control
		axios.get('api/time-control/get-last', {
			cancelToken: cancelTokenSource.token
		}).then((response) => {
			setLastTimeControl(response.data);
		}).catch(() => {});

		// Workorder counts
		if (user?.has_progapp) {
			axios.get('api/workorders/counts', {
				cancelToken: cancelTokenSource.token
			}).then((response) => {
				setWorkorderCounts(response.data);
			}).catch(() => {});
		}

		// Logistics counts
		if (user?.has_logistica) {
			axios.get('api/logistics/counts', {
				params: { section: 'supply' },
				cancelToken: cancelTokenSource.token
			}).then((response) => {
				setLogisticsCounts(response.data);
			}).catch(() => {});

			axios.get('api/logistics/counts', {
				params: { section: 'logistics' },
				cancelToken: cancelTokenSource.token
			}).then((response) => {
				setLogisticsNonSupplyCounts(response.data);
			}).catch(() => {});
		}
	}, [user]);

	useEffect(() => {
		cancelTokenSource = axios.CancelToken.source();
		loadDashboard();

		// Poll every 60 seconds
		const interval = setInterval(() => {
			loadDashboard();
		}, 60000);

		return function cleanup() {
			cancelTokenSource.cancel();
			clearInterval(interval);
		}
	}, [loadDashboard]);

	const logout = () => {
		if (!window.confirm('¿Seguro que quieres cerrar sesión?')) return;
		dispatch({type: 'setUser', data: ''});
		dispatch({type: 'setToken', data: ''});
	};

	// Last time control icon
	let lastTimeControlIcon = "\u26A0";
	if (lastTimeControl && lastTimeControl.action === "in") lastTimeControlIcon = "\uD83D\uDD50";

	return (
		<ContainerStyled className="container" style={{maxWidth: 500}}>
			<div className="text-center">
				<h4 className="p-0 m-0 my-3">Hola, {user?.name}</h4>
			</div>

			{/* Horario */}
			<div className="section-card">
				<Link to="/time-control" className="btn btn-primary btn-lg menu-btn">
					<span>HORARIO {lastTimeControlIcon}</span>
				</Link>
			</div>

			{/* Calendario */}
			<div className="section-card">
				<Link to="/calendar" className="btn btn-dark btn-lg menu-btn">
					<span>CALENDARIO</span>
				</Link>
			</div>

			{/* Partes de trabajo */}
			{user?.has_progapp &&
				<div className="section-card">
					<div className="section-title">Instalación / Mantenimiento</div>
					<Link to="/open-plans" className="btn btn-success menu-btn">
						<span>Planes abiertos</span>
						{workorderCounts && <span className="count-badge">{workorderCounts.open ?? 0}</span>}
					</Link>
					<Link to="/closed-plans" className="btn btn-secondary menu-btn">
						<span>Planes cerrados</span>
						{workorderCounts && <span className="count-badge">{workorderCounts.closed ?? 0}</span>}
					</Link>
				</div>
			}

			{/* Suministro */}
			{user?.has_logistica &&
				<div className="section-card">
					<div className="section-title">Suministro</div>
					<Link to="/logistics-plans/pendiente_preparar?section=supply" className="btn btn-warning menu-btn text-dark">
						<span>Pendiente preparar</span>
						{logisticsCounts && <span className="count-badge">{logisticsCounts.pendiente_preparar ?? 0}</span>}
					</Link>
					<Link to="/logistics-plans/en_preparacion?section=supply" className="btn btn-info menu-btn text-white">
						<span>En preparación</span>
						{logisticsCounts && <span className="count-badge">{logisticsCounts.en_preparacion ?? 0}</span>}
					</Link>
					<Link to="/logistics-plans/preparado?section=supply" className="btn btn-success menu-btn">
						<span>Preparado</span>
						{logisticsCounts && <span className="count-badge">{logisticsCounts.preparado ?? 0}</span>}
					</Link>
					<Link to="/logistics-plans/enviado?section=supply" className="btn btn-primary menu-btn">
						<span>Enviado</span>
						{logisticsCounts && <span className="count-badge">{logisticsCounts.enviado ?? 0}</span>}
					</Link>
				</div>
			}

			{/* Logística */}
			{user?.has_logistica &&
				<div className="section-card">
					<div className="section-title">Logística</div>
					<Link to="/logistics-plans/pendiente_preparar?section=logistics" className="btn btn-warning menu-btn text-dark">
						<span>Pendiente preparar</span>
						{logisticsNonSupplyCounts && <span className="count-badge">{logisticsNonSupplyCounts.pendiente_preparar ?? 0}</span>}
					</Link>
					<Link to="/logistics-plans/en_preparacion?section=logistics" className="btn btn-info menu-btn text-white">
						<span>En preparación</span>
						{logisticsNonSupplyCounts && <span className="count-badge">{logisticsNonSupplyCounts.en_preparacion ?? 0}</span>}
					</Link>
					<Link to="/logistics-plans/preparado?section=logistics" className="btn btn-success menu-btn">
						<span>Preparado</span>
						{logisticsNonSupplyCounts && <span className="count-badge">{logisticsNonSupplyCounts.preparado ?? 0}</span>}
					</Link>
					<Link to="/logistics-plans/enviado?section=logistics" className="btn btn-primary menu-btn">
						<span>Enviado</span>
						{logisticsNonSupplyCounts && <span className="count-badge">{logisticsNonSupplyCounts.enviado ?? 0}</span>}
					</Link>
				</div>
			}

			{/* Logout */}
			<div className="mb-4">
				<button className="btn btn-outline-danger logout-btn" onClick={logout}>CERRAR SESIÓN</button>
			</div>
		</ContainerStyled>
	);
}
